import { logError } from '../../utils/logging';

/* global Office, Word */

/**
 * Utility class for exporting Word documents
 */
export class DocumentExporter {
  /**
   * Checks if Office.js is available
   */
  private static isOfficeAvailable(): boolean {
    return typeof Office !== 'undefined' && typeof Office.context !== 'undefined';
  }

  /**
   * Extracts the filename from a document URL or file path.
   * Word on Windows may return backslash paths or full paths in the title property.
   */
  private static extractFilenameFromPath(pathOrUrl: string): string {
    let normalized = pathOrUrl.trim();

    if (normalized.toLowerCase().startsWith('file://')) {
      normalized = normalized.slice('file://'.length);
      // file:///C:/path on Windows — drop leading slash before drive letter
      if (/^\/[a-zA-Z]:/.test(normalized)) {
        normalized = normalized.slice(1);
      }
    }

    const segments = normalized.split(/[/\\]/).filter((segment) => segment.length > 0);
    const basename = segments.length > 0 ? segments[segments.length - 1] : normalized;

    try {
      return decodeURIComponent(basename);
    } catch {
      return basename;
    }
  }

  /**
   * OLE / unset Word dates show up as epoch or 1899 — treat those as missing.
   */
  private static toIsoDate(value: Date | string | number | null | undefined): string | null {
    if (value == null || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    const ms = date.getTime();
    if (!Number.isFinite(ms)) return null;
    if (date.getUTCFullYear() < 1980) return null;
    return date.toISOString();
  }

  /**
   * Basename + timestamps from the open document (never the local folder path).
   * lastSaveTime is the primary snapshot id; createdAt is the unsaved fallback.
   */
  static async getDocumentIdentity(): Promise<{
    name: string;
    lastSaveTime: string | null;
    createdAt: string | null;
  }> {
    const fallbackName = 'document.docx';
    if (!this.isOfficeAvailable()) {
      return { name: fallbackName, lastSaveTime: null, createdAt: null };
    }

    const nameFromUrl = Office.context.document.url
      ? this.extractFilenameFromPath(Office.context.document.url)
      : null;

    try {
      return await Word.run(async (context) => {
        const properties = context.document.properties;
        properties.load(['title', 'lastSaveTime', 'creationDate']);
        await context.sync();

        let name = nameFromUrl;
        if (!name) {
          const title = properties.title || 'Untitled Document';
          name = DocumentExporter.extractFilenameFromPath(title);
          if (!name.toLowerCase().endsWith('.docx')) {
            name = `${name}.docx`;
          }
        }

        return {
          name,
          lastSaveTime: DocumentExporter.toIsoDate(properties.lastSaveTime),
          createdAt: DocumentExporter.toIsoDate(properties.creationDate),
        };
      });
    } catch (error) {
      logError('Failed to get document identity', error);
      return {
        name: nameFromUrl || fallbackName,
        lastSaveTime: null,
        createdAt: null,
      };
    }
  }

  /**
   * Gets the document filename from the Word document
   * @returns The document filename (with extension) or a default name
   */
  static async getDocumentName(): Promise<string> {
    if (!this.isOfficeAvailable()) {
      // Return default name when Office.js is not available (testing mode)
      return 'document.docx';
    }

    try {
      // Try to get the document URL which contains the filename
      if (Office.context.document.url) {
        return this.extractFilenameFromPath(Office.context.document.url);
      }

      // Fallback: Use Word API to get document properties
      return await Word.run(async (context) => {
        const properties = context.document.properties;
        properties.load('title');
        await context.sync();
        
        // Use title if available, otherwise use default
        const title = properties.title || 'Untitled Document';
        const filename = DocumentExporter.extractFilenameFromPath(title);
        
        // Ensure it has .docx extension
        if (!filename.toLowerCase().endsWith('.docx')) {
          return `${filename}.docx`;
        }
        
        return filename;
      });
    } catch (error) {
      logError('Failed to get document name', error);
      return 'document.docx';
    }
  }

  /**
   * Gets the document filename without extension
   * @returns The document filename without extension
   */
  static async getDocumentNameWithoutExtension(): Promise<string> {
    const filename = await this.getDocumentName();
    return filename.replace(/\.docx$/i, '');
  }

  /**
   * Exports the Word document as a File/Blob for upload
   * @param filename The desired filename for the exported file
   * @returns A File object containing the document data
   */
  static async getDocumentAsFile(filename: string): Promise<File> {
    if (!this.isOfficeAvailable()) {
      // Create a mock file for testing when Office.js is not available
      const mockContent = 'Mock Word document content for testing';
      const blob = new Blob([mockContent], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      return new File([blob], filename, { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
    }

    try {
      return new Promise<File>((resolve, reject) => {
        // Use Office.context.document.getFileAsync to get the document as a blob
        Office.context.document.getFileAsync(
          Office.FileType.Compressed,
          { sliceSize: 65536 }, // 64KB slices
          (result: Office.AsyncResult<Office.File>) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              reject(new Error(result.error?.message || 'Failed to get file'));
              return;
            }

            const file = result.value;
            const sliceCount = file.sliceCount;
            const slices: Uint8Array[] = [];

            // Read all slices
            let currentSlice = 0;

            const getSliceAsync = () => {
              file.getSliceAsync(currentSlice, (sliceResult: Office.AsyncResult<Office.FileSlice>) => {
                if (sliceResult.status === Office.AsyncResultStatus.Failed) {
                  file.closeAsync();
                  reject(new Error(sliceResult.error?.message || 'Failed to get slice'));
                  return;
                }

                // Store the slice data
                slices.push(new Uint8Array(sliceResult.value.data));
                currentSlice++;

                if (currentSlice < sliceCount) {
                  // Get next slice
                  getSliceAsync();
                } else {
                  // All slices retrieved, combine them
                  file.closeAsync();
                  
                  // Calculate total length
                  const totalLength = slices.reduce((acc, slice) => acc + slice.length, 0);
                  
                  // Combine all slices into a single array
                  const combined = new Uint8Array(totalLength);
                  let offset = 0;
                  
                  for (const slice of slices) {
                    combined.set(slice, offset);
                    offset += slice.length;
                  }
                  
                  // Create blob and file
                  const blob = new Blob([combined], { 
                    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
                  });
                  
                  const fileObject = new File([blob], filename, { 
                    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
                  });
                  
                  resolve(fileObject);
                }
              });
            };

            // Start reading slices
            getSliceAsync();
          }
        );
      });
    } catch (error) {
      logError('Failed to export document as file', error);
      throw error;
    }
  }
}

